import React, { useEffect, useRef } from 'react';
import { Mat4, Vec3, clamp, deg, easeInOutCubic, hingeX, mat4 } from '../gl/math';
import { MeshBuilder } from '../gl/geometry';
import { Draw, GpuMesh, Material, Renderer } from '../gl/renderer';
import { MaterialId, PartDef, getParts } from './parts';
import {
  Act, HP_RATIO, LOOP_START, PartState, SETTLE_S, SceneState, actAt, blend, loopTime, sample, spoolSpeed,
} from './choreography';

/*
 * The 3D startup loader: canvas + requestAnimationFrame loop over the pure
 * timeline in choreography.ts. Unlike the 2D blueprint this needs a JS frame
 * loop - there is no CSS equivalent for a depth-sorted 3D scene.
 *
 * Falls back (onFallback) when WebGL2 or the shaders are unavailable, the
 * context is lost, or the GPU cannot hold a usable frame rate.
 */

interface Props {
  /** Data is in: close up from wherever the show is, then onSettled. */
  finishing: boolean;
  onSettled: () => void;
  onAct?: (act: Act) => void;
  onFallback: () => void;
}

const BUILT_KEY = 'ds-startup-built';
const MAX_DPR = 1.75;
/** Aspect the choreography's camera path was framed for (1200:640). */
const DESIGN_ASPECT = 1.875;
/** Breathing room so the widest exploded poses never touch the canvas edge. */
const FIT_MARGIN = 1.12;
const INTRO_S = 0.8;

const readBuilt = () => {
  try {
    return window.sessionStorage.getItem(BUILT_KEY) === '1';
  } catch {
    return false;
  }
};
const markBuilt = () => {
  try {
    window.sessionStorage.setItem(BUILT_KEY, '1');
  } catch {
    /* storage unavailable - the build simply plays again */
  }
};

/** Timeline playback rate. The choreography is authored at 1x; it ships at 3x. */
const PLAYBACK_SPEED = 3;

/** Dev-only review aids: `&t=12.5` freezes the timeline, `&speed=0.5` overrides the playback rate. */
function devParams() {
  if (process.env.NODE_ENV !== 'development') return { freeze: null as number | null, speed: PLAYBACK_SPEED };
  const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const t = q.get('t');
  const speed = Number(q.get('speed') ?? PLAYBACK_SPEED);
  return { freeze: t !== null && !Number.isNaN(Number(t)) ? Number(t) : null, speed: speed > 0 ? speed : PLAYBACK_SPEED };
}

/** Parses a token colour (#rgb, #rrggbb, rgb(), rgba()) to linear RGB. */
function tokenColor(el: Element, name: string): Vec3 {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  let rgb: number[] = [0.5, 0.5, 0.5];
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
    rgb = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16) / 255);
  } else {
    const m = raw.match(/rgba?\(([^)]+)\)/);
    if (m) rgb = m[1].split(/[\s,/]+/).slice(0, 3).map(v => Number(v) / 255);
  }
  return rgb.map(c => Math.pow(c, 2.2)) as Vec3;
}

const mix = (a: Vec3, b: Vec3, t: number): Vec3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const mul = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];

function readPalette(el: Element) {
  const c = (n: string) => tokenColor(el, `--ds-splash-${n}`);
  const metal = c('metal');
  const metalDark = c('metal-dark');
  const copper = c('copper');
  const gold = c('gold');
  const accent = c('accent');
  const line = c('line');
  const bg = c('bg');
  const materials: Record<MaterialId, Material> = {
    metal: { base: metal, spec: 0.9, gloss: 60, metal: 0.85, rim: 0.3 },
    metalDark: { base: metalDark, spec: 0.6, gloss: 40, metal: 0.7, rim: 0.3 },
    hot: { base: mul(mix(metal, copper, 0.4), 0.85), spec: 0.8, gloss: 44, metal: 0.8, rim: 0.3 },
    gold: { base: gold, spec: 1, gloss: 70, metal: 0.9, rim: 0.4, emissive: mul(gold, 0.06) },
    copper: { base: copper, spec: 0.9, gloss: 50, metal: 0.85, rim: 0.4 },
    composite: { base: c('composite'), spec: 0.45, gloss: 30, metal: 0.25, rim: 0.4 },
    // Nacelle skin in Saga gold, glossy paint with a slight self-glow.
    livery: { base: c('livery'), spec: 1.2, gloss: 70, metal: 0.45, rim: 0.25, emissive: mul(c('livery'), 0.28) },
    harness: { base: c('harness'), spec: 0.2, gloss: 12, metal: 0, rim: 0.45 },
    flame: { base: accent, spec: 0, gloss: 1, metal: 0, rim: 0, emissive: mul(accent, 1.4) },
  };
  return {
    materials,
    edgeCol: c('edge'),
    // Cool sky / dark bounce derived from the navy background, lifted towards
    // the (white) line colour so the metal reads as steel, not blue.
    sky: mix(mul(bg, 2.2), line, 0.1),
    ground: mix(mul(bg, 0.9), line, 0.02),
  };
}

const KEY_DIR: Vec3 = [-0.45, 0.8, 0.55];

function modelMatrix(p: PartDef, s: PartState, lp: number, hp: number): Mat4 {
  const base = p.base ?? mat4.identity();
  const tr = mat4.translation(s.t[0], s.t[1], s.t[2]);
  if (p.spool) {
    const spin = mat4.rotationX(p.spool === 'lp' ? lp : hp);
    return mat4.multiply(spin, mat4.multiply(tr, base));
  }
  if (p.hinge && s.hinge !== 0) {
    return mat4.multiply(tr, mat4.multiply(hingeX(p.hinge[0], p.hinge[1], s.hinge), base));
  }
  return mat4.multiply(tr, base);
}

const EngineScene3D = ({ finishing, onSettled, onAct, onFallback }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const finishingRef = useRef(finishing);
  finishingRef.current = finishing;
  const cb = useRef({ onSettled, onAct, onFallback });
  cb.current = { onSettled, onAct, onFallback };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: Renderer;
    try {
      renderer = new Renderer(canvas);
    } catch {
      cb.current.onFallback();
      return;
    }

    let raf = 0;
    let dead = false;
    const fail = () => {
      if (dead) return;
      dead = true;
      cancelAnimationFrame(raf);
      cb.current.onFallback();
    };

    let ro: ResizeObserver | null = null;
    const onLost = (e: Event) => {
      e.preventDefault();
      fail();
    };
    // Must never throw: it runs on unmount and on every failure path.
    const cleanup = () => {
      dead = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      canvas.removeEventListener('webglcontextlost', onLost);
      try {
        renderer.dispose();
      } catch {
        /* context already gone */
      }
    };

    // Any throw during setup falls back to the 2D blueprint, which finishes on
    // plain timers. The loader must never be left without a way to settle.
    let parts: PartDef[];
    const meshes = new Map<MeshBuilder, GpuMesh>();
    let palette: ReturnType<typeof readPalette>;
    let dev: ReturnType<typeof devParams>;
    let built: boolean;
    let start: number;
    let settled: SceneState;
    let dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    let skipMinor = false;
    const resize = () => renderer.resize(canvas.clientWidth, canvas.clientHeight, dpr);
    try {
      parts = getParts();
      for (const p of parts) if (!meshes.has(p.mesh)) meshes.set(p.mesh, renderer.upload(p.mesh.build()));
      palette = readPalette(canvas);
      dev = devParams();
      built = readBuilt();
      start = built ? LOOP_START : 0;
      settled = sample(parts, LOOP_START);
      ro = new ResizeObserver(() => {
        try {
          resize();
        } catch {
          fail();
        }
      });
      ro.observe(canvas);
      resize();
      canvas.addEventListener('webglcontextlost', onLost);
    } catch {
      cleanup();
      cb.current.onFallback();
      return;
    }

    let clock = 0;
    let last = performance.now();
    let lp = 0;
    let hp = 0;
    let act: Act | null = null;
    let settleFrom: SceneState | null = null;
    let settleK = 0;
    let done = false;
    let builtMarked = built;
    const perf: number[] = [];
    let frames = 0;

    // A throw anywhere in a frame falls back to 2D instead of silently killing
    // the loop, which would leave the loader up with nothing to settle it.
    const frame = (now: number) => {
      if (dead) return;
      try {
        renderFrame(now);
      } catch {
        fail();
        return;
      }
      if (!dead) raf = requestAnimationFrame(frame);
    };

    const renderFrame = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      frames++;

      // Adaptive quality: after warm-up, average ~60 frames; step down, then bail out.
      if (frames > 10 && document.visibilityState === 'visible') {
        perf.push(dt);
        if (perf.length >= 60) {
          const avg = perf.reduce((a, b) => a + b, 0) / perf.length;
          perf.length = 0;
          if (avg > 0.024 && dpr > 1) {
            dpr = 1;
            resize();
          } else if (avg > 0.03 && !skipMinor) {
            skipMinor = true;
          } else if (avg > 0.045) {
            fail();
            return;
          }
        }
      }

      if (dev.freeze === null) clock += dt * dev.speed;
      const t = dev.freeze ?? loopTime(clock, start);
      if (!builtMarked && t >= LOOP_START) {
        builtMarked = true;
        markBuilt();
      }

      let state = sample(parts, t);
      if (built && dev.freeze === null) {
        const k = clamp(clock / INTRO_S);
        if (k < 1) state.parts.forEach(ps => { ps.reveal *= k; ps.edge *= k; });
      }
      if (finishingRef.current) {
        if (!settleFrom) settleFrom = state;
        settleK = Math.min(1, settleK + dt / SETTLE_S);
        state = blend(settleFrom, settled, easeInOutCubic(settleK));
        if (settleK >= 1 && !done) {
          done = true;
          markBuilt();
          cb.current.onSettled();
        }
      }

      const speed = finishingRef.current ? spoolSpeed(LOOP_START) : spoolSpeed(t);
      if (dev.freeze === null) {
        lp += speed * dt;
        hp += speed * HP_RATIO * dt;
      }

      const a = finishingRef.current ? 'hero' : actAt(t);
      if (a !== act) {
        act = a;
        cb.current.onAct?.(a);
      }

      const c = state.cam;
      const target: Vec3 = [c.tx, c.ty, 0];
      const az = deg(c.az), el = deg(c.el);
      const aspect = canvas.width / Math.max(1, canvas.height);
      // The show is framed for DESIGN_ASPECT; narrower canvases pull the
      // camera back instead of cropping the sides.
      const dist = c.dist * FIT_MARGIN * Math.max(1, DESIGN_ASPECT / aspect);
      const eye: Vec3 = [
        target[0] - dist * Math.cos(el) * Math.cos(az),
        target[1] + dist * Math.sin(el),
        target[2] + dist * Math.cos(el) * Math.sin(az),
      ];
      const viewProj = mat4.multiply(mat4.perspective(deg(30), aspect, 0.1, 80), mat4.lookAt(eye, target, [0, 1, 0]));

      const draws: Draw[] = [];
      parts.forEach((p, i) => {
        if (skipMinor && p.minor) return;
        const ps = state.parts[i];
        draws.push({
          mesh: meshes.get(p.mesh) as GpuMesh,
          model: modelMatrix(p, ps, lp, hp),
          material: palette.materials[p.material],
          reveal: ps.reveal,
          edge: ps.edge,
          grow: ps.grow,
        });
      });
      renderer.frame({
        viewProj, eye, keyDir: KEY_DIR, sky: palette.sky, ground: palette.ground, edgeCol: palette.edgeCol,
        scanX: state.scanX, scan: state.scan,
      }, draws);
    };
    raf = requestAnimationFrame(frame);

    return cleanup;
  }, []);

  return <canvas ref={canvasRef} className="sl-canvas" aria-hidden="true" />;
};

export default EngineScene3D;
